import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, api, refreshSession, setAccessToken, setSessionLostHandler } from './client';
import type { Policy, User } from './types';

type LoginOutcome =
  | { kind: 'signed_in' }
  | { kind: 'mfa_required'; challengeToken: string; enrolment?: { uri: string; secret: string } }
  | { kind: 'error'; message: string };

type SessionValue = {
  user: User | null;
  policy: Policy | null;
  loading: boolean;
  /** True once a sign-in has happened recently enough to publish or revoke. */
  recentAuth: boolean;
  signIn: (email: string, password: string) => Promise<LoginOutcome>;
  verifyMfa: (challengeToken: string, code: string) => Promise<LoginOutcome>;
  signOut: () => Promise<void>;
  markReauthNeeded: () => void;
};

const SessionContext = createContext<SessionValue | null>(null);

type Me = User & { recent_auth: boolean };

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [recentAuth, setRecentAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadContext = useCallback(async () => {
    const [me, agencyPolicy] = await Promise.all([
      api.get<Me>('/auth/me'),
      api.get<Policy>('/policy').catch(() => null),
    ]);
    setUser(me);
    setRecentAuth(me.recent_auth);
    if (agencyPolicy) setPolicy(agencyPolicy);
  }, []);

  useEffect(() => {
    setSessionLostHandler(() => {
      setUser(null);
      setPolicy(null);
      setAccessToken(null);
    });

    // On load there is no access token in memory — it never persists. If the
    // httpOnly refresh cookie is still valid, this quietly restores the
    // session; if not, the sign-in screen appears. Either way nothing
    // JavaScript-readable was stored between visits.
    void (async () => {
      if (await refreshSession()) {
        await loadContext().catch(() => setUser(null));
      }
      setLoading(false);
    })();

    return () => setSessionLostHandler(null);
  }, [loadContext]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      try {
        const response = await api.post<{ access_token: string; user: User }>('/auth/login', {
          email,
          password,
          client: 'web',
        });
        setAccessToken(response.access_token);
        await loadContext();
        return { kind: 'signed_in' };
      } catch (caught) {
        const error = caught as ApiError;
        if (error.code === 'mfa_required') {
          const uri = error.details.provisioning_uri as string | undefined;
          const secret = error.details.secret as string | undefined;
          return {
            kind: 'mfa_required',
            challengeToken: error.details.challenge_token as string,
            enrolment: uri && secret ? { uri, secret } : undefined,
          };
        }
        return { kind: 'error', message: error.message };
      }
    },
    [loadContext],
  );

  const verifyMfa = useCallback(
    async (challengeToken: string, code: string): Promise<LoginOutcome> => {
      try {
        const response = await api.post<{ access_token: string; user: User }>('/auth/mfa/verify', {
          challenge_token: challengeToken,
          code,
          client: 'web',
        });
        setAccessToken(response.access_token);
        await loadContext();
        return { kind: 'signed_in' };
      } catch (caught) {
        return { kind: 'error', message: (caught as ApiError).message };
      }
    },
    [loadContext],
  );

  const signOut = useCallback(async () => {
    await api.post('/auth/logout', {}).catch(() => undefined);
    setAccessToken(null);
    setUser(null);
    setPolicy(null);
    setRecentAuth(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      policy,
      loading,
      recentAuth,
      signIn,
      verifyMfa,
      signOut,
      markReauthNeeded: () => setRecentAuth(false),
    }),
    [user, policy, loading, recentAuth, signIn, verifyMfa, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
